import {useState} from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {Button, IconButton, Link, Tooltip} from '@mui/material';
import {isAxiosError} from 'axios';
import {MRT_ColumnDef} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  OnboardingExtDataConfig,
  OnboardingType,
  useRemoveOnboardingExtDataConfig,
} from '@entities/onboarding';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  MrTable,
  openConfirmation,
  TableRowActionsContainer,
} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';
import {Header} from '@widgets/common';
import {UtmConfigDialog} from './UtmConfigDialog';

import {COLOR__WHITE} from '@/theme/colors';

const columns: MRT_ColumnDef<OnboardingExtDataConfig>[] = [
  {accessorKey: 'extName', header: 'UTM parameter', size: 160},
  {accessorKey: 'extValue', header: 'UTM value', size: 160},
  {
    accessorKey: 'config',
    header: 'Config',
    Cell: ({row}) => (
      <Link
        href={row.original.config}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.original.config}
      </Link>
    ),
  },
  {accessorKey: 'priority', header: 'Priority', size: 100},
];

interface Props {
  type: OnboardingType;
  configs: OnboardingExtDataConfig[];
}

export const UtmConfigs = ({type, configs}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const [selectedConfig, setSelectedConfig] = useState<{
    config?: OnboardingExtDataConfig;
  } | null>(null);
  const {mutateAsync: removeConfig, isPending: isRemoving} =
    useRemoveOnboardingExtDataConfig();

  const handleDelete = async (config: OnboardingExtDataConfig) => {
    if (isRemoving) return;

    try {
      await openConfirmation({
        title: 'Remove UTM config',
        content: `Remove the ${config.extName} = ${config.extValue} config?`,
        okButtonText: 'Remove',
      });
    } catch {
      return;
    }

    try {
      await removeConfig({type, configId: config.id});
      enqueueSnackbar('UTM config removed successfully', {variant: 'success'});
    } catch (error) {
      const message = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not remove UTM config. ${message}`, {
        variant: 'error',
      });
    }
  };

  return (
    <CardBox bgcolor={COLOR__WHITE} mb={3}>
      <Header
        title="UTM Configs"
        mb={2}
        isBackButtonShown={false}
        rightPart={
          <Button onClick={() => setSelectedConfig({})} disabled={isRemoving}>
            Add UTM config
          </Button>
        }
      />
      <MrTable
        columns={columns}
        data={configs}
        renderRowActions={({row}) => (
          <TableRowActionsContainer>
            <Tooltip title="Edit UTM config">
              <IconButton
                size="small"
                disabled={isRemoving}
                onClick={() => setSelectedConfig({config: row.original})}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove UTM config">
              <IconButton
                size="small"
                disabled={isRemoving}
                onClick={() => void handleDelete(row.original)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </TableRowActionsContainer>
        )}
        {...entityTableCommonProps}
      />
      {selectedConfig && (
        <UtmConfigDialog
          type={type}
          config={selectedConfig.config}
          onClose={() => setSelectedConfig(null)}
        />
      )}
    </CardBox>
  );
};
